import { useState, useEffect, forwardRef, useImperativeHandle, useRef } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import api from "../../../api/Api";
import backendToInputDate from "../../../utils/backendToInputDate";
import { RequiredLabel } from "../../../components/RequiredLabel/RequiredLabel";
import { ToastContainer } from "react-toastify";
import { Autocomplete, TextField } from '@mui/material';

const AssignForm = forwardRef(({ initialData = {}, onSubmit = () => {}, isEdit = false }, ref) => {
  const [employees, setEmployees] = useState([]);
  const [assets, setAssets] = useState([]);
  const [statuses, setStatuses] = useState([]);

  const formikRef = useRef(null);

  // Expose submitForm to parent
  useImperativeHandle(ref, () => ({
    submitForm: () => {
      if (formikRef.current) formikRef.current.submitForm();
    }
  }));

  // Fetch dropdowns
  useEffect(() => {
    api.get("/api/employees/drop")
      .then(res => setEmployees(res.data.response || []))
      .catch(() => setEmployees([]));

    // api.get("/api/assets/drop")
    //   .then(res => setAssets(res.data.response || []))
    //   .catch(() => setAssets([]));

    api.get("/api/assignments/status")
      .then(res => setStatuses(res.data.response || []))
      .catch(() => setStatuses([]));
  }, []);

 const fetchAssetsByShift = async (shift, location) => {
      try {
        const res = await api.get("/api/assets/drop", {
          params: { shift , location}
        });
        setAssets(res.data.response || []);
      } catch (err) {
        setAssets([]);
      }
    };

  // Validation schema
  const validationSchema = Yup.object().shape({
    employeeIdenty: Yup.string().required("Employee is required"),
    assetIdenties: Yup.array().min(1, "At least one asset is required"),
    assignedDate: Yup.string().required("Assigned Date is required"),
    assignStatus: Yup.string().required("Assignment Status is required"),
  });

  // Formik initial values
  const initialValues = {
    employeeIdenty: initialData.employeeIdenty?.identy || "",
    assetIdenties: isEdit
      ? initialData.assetIdenty
        ? [initialData.assetIdenty.identy] // wrap single asset in array for Formik
        : []
      : initialData.assetIdenties || [],
    assignedDate: backendToInputDate(initialData.assignedDate) || "",
    returnedDate: backendToInputDate(initialData.returnedDate) || "",
    assignStatus: initialData.assignStatus || "ASSIGNED",
    notes: initialData.notes || "",
    remarks: initialData.remarks || "",
  };

     const employeeOptions = employees.map(emp => ({
      label: `${emp.employeeName} (${emp.employeeId})`,
      value: emp.identy,
      shift: emp.shift ,
      location: emp.location,        
    }));

  const assetOptions = assets.map(a => ({
    label: `${a.assetName} (${a.assetId})`,
    value: a.identy,
    status: a.assetStatus
  }));

  return (
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={onSubmit}
      >
        {({ values, setFieldValue }) => (
          <Form className="border p-3 rounded shadow-sm bg-white">

            {/* EMPLOYEE & ASSET */}
            <div className="row mt-3">
              {/* Employee */}
              <div className="col mb-3">
                <RequiredLabel label="Select Employee" />

                {isEdit ? (
                  <input
                    type="text"
                    className="form-control"
                    value={initialData.employeeIdenty?.employeeName || ""}
                    disabled
                  />
                ) : (
                  <Autocomplete
                    options={employeeOptions}
                    value={
                      employeeOptions.find(
                        opt => opt.value === values.employeeIdenty
                      ) || null
                    }
                    onChange={(e, newValue) => {
                      if (newValue) {
                        setFieldValue("employeeIdenty", newValue.value);

                        // fetch assets based on shift
                        if (newValue.shift && newValue.location) {
                          fetchAssetsByShift(newValue.shift, newValue.location);
                        }

                        // reset assets
                        setFieldValue("assetIdenties", []);
                      } else {
                        setFieldValue("employeeIdenty", "");
                        setAssets([]);
                        setFieldValue("assetIdenties", []);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        placeholder="-- Select Employee --"
                      />
                    )}
                  />
                )}
                <ErrorMessage name="employeeIdenty" component="div" className="text-danger" />
              </div>

              {/* Asset */}
              <div className="col mb-3">
                <RequiredLabel label="Select Asset" />

                {isEdit ? (
                  <input
                    type="text"
                    className="form-control"
                    value={initialData.assetIdenty?.assetName || ""}
                    disabled
                  />
                ) : (
                  <Autocomplete
                    multiple
                    disabled={!values.employeeIdenty}
                    options={assetOptions.filter(
                      opt => !values.assetIdenties.includes(opt.value)
                    )}
                    value={assetOptions.filter(
                      opt => values.assetIdenties.includes(opt.value)
                    )}
                    onChange={(e, newValue) => {
                      setFieldValue(
                        "assetIdenties",
                        newValue.map(opt => opt.value)
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        placeholder={
                          values.employeeIdenty
                            ? "-- Select Assets --"
                            : "Select employee first"
                        }
                      />
                    )}
                  />
                )}
                <ErrorMessage name="assetIdenties" component="div" className="text-danger" />
              </div>
            </div>

            {/* DATES */}
            <div className="row">
              <div className="col mb-3">
                <RequiredLabel label="Assigned Date" />
                <Field type="date" name="assignedDate" className="form-control" />
                <ErrorMessage name="assignedDate" component="div" className="text-danger" />
              </div>

              <div className="col mb-3">
                <label className="form-label">Expected Return Date</label>
                <Field type="date" name="returnedDate" className="form-control" />
              </div>
            </div>

            {/* STATUS */}
            <div className="mb-3">
              <RequiredLabel label="Assignment Status" />
              <Field as="select" name="assignStatus" className="form-select" disabled={isEdit}>
                <option value="">-- Select Status --</option>
                {statuses.map(status => (
                  <option key={status} value={status}>
                    {status.replace(/_/g, " ")}
                  </option>
                ))}
              </Field>
              <ErrorMessage name="assignStatus" component="div" className="text-danger" />
            </div>

            {/* NOTES */}
            <div className="mb-3">
              <label className="form-label">Notes</label>
              <Field as="textarea" name="notes" className="form-control" rows="2" />
            </div>

            {/* REMARKS */}
            <div className="mb-3">
              <label className="form-label">Remarks</label>
              <Field as="textarea" name="remarks" className="form-control" rows="3" />
            </div>

          </Form>
        )}
      </Formik>
    );
  }
);

export default AssignForm;